'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { urlFor, unwrapImage, resolveImageSrcSet } from '@/sanity/lib/image';
import { PortableText } from '@portabletext/react';
import { CountryFAQ } from '@/components/country';
import { buildFaqJsonLd, serializeJsonLd } from '@/lib/structuredData';
import GfSectionMark from '@/components/common/GfSectionMark';
import GfArticleCards from '@/components/common/GfArticleCards';
import GfBento from '@/components/common/GfBento';
import GfSubnav from '@/components/common/GfSubnav';
import GfCta from '@/components/common/GfCta';
import { CityInteractiveMap } from '@/components/maps';
import { distanceKm } from '@/utils/distance';
import CityHotels from '@/components/accommodation/CityHotels';
import CityTours from '@/components/tours/CityTours';
import { buildPoiUrl } from '@/utils/buildPoiUrl';
import { replacePlaceholdersDeep } from '@/lib/content/placeholders';
import { buildProviderLink, isExternalLink } from '@/utils/blog/providerLinkHelpers';
import WeatherBlock from '@/components/city/WeatherBlock';
import AffiliateLink from '@/components/common/AffiliateLink';
import { TbDeviceMobile, TbCar, TbBed, TbCompass } from 'react-icons/tb';
import { t } from '@/i18n/translations';
import { buildAffiliateSubId, getAffiliateMetadata } from '@/lib/affiliates/metadata';
import { wrapAffiliateLink } from '@/lib/affiliates/proxy';
import { declineCityName } from '@/lib/content/declension';

// ── Hero "facts" mosaic helpers (Регион / Когда ехать / Аэропорт / Язык / Время) ──
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const capFirst = (s) => (typeof s === 'string' && s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// bestMonths is stored in calendar order, so a winter season (e.g. Qatar
// nov–mar) or a split season (e.g. Switzerland ski + summer) is non-contiguous.
// Detect contiguous runs on a circular calendar and render each as a range.
// `names` = localized month names, `yearRound` = localized "year-round" label.
const formatSeason = (monthKeys, names, yearRound) => {
    const present = (Array.isArray(monthKeys) ? monthKeys : [])
        .map((k) => MONTH_KEYS.indexOf(k))
        .filter((i) => i >= 0);
    if (!present.length) return null;
    if (present.length >= 12) return yearRound;

    const set = new Set(present);
    // A block starts at a present month whose previous month (mod 12) is absent.
    const starts = present.filter((i) => !set.has((i + 11) % 12)).sort((a, b) => a - b);
    const blocks = starts.map((start) => {
        let end = start;
        while (set.has((end + 1) % 12)) end = (end + 1) % 12;
        return start === end ? names[start] : `${names[start]} – ${names[end]}`;
    });
    return capFirst(blocks.join(', '));
};
// "Аэропорт Тиват" / "Tivat Airport" → "Тиват" / "Tivat"
const shortAirportName = (name) => (typeof name === 'string'
    ? name.replace(/\bаэропорт\b/gi, '').replace(/\bairport\b/gi, '').replace(/\s{2,}/g, ' ').trim()
    : name);

export default function CityPageContent({ cityPage, lang = 'ru', gptData = null }) {
    // Состояние для управления видимостью городов рядом
    const defaultVisibleCities = 8;
    const [visibleCitiesCount, setVisibleCitiesCount] = useState(defaultVisibleCities);
    const [allCitiesInCountry, setAllCitiesInCountry] = useState([]);
    const [isLoadingAllCities, setIsLoadingAllCities] = useState(false);

    // Функция для загрузки всех городов страны
    const loadAllCitiesInCountry = async () => {
        if (allCitiesInCountry.length > 0) {
            // Если уже загружены, просто переключаем отображение
            setVisibleCitiesCount(allCitiesInCountry.length);
            return;
        }

        setIsLoadingAllCities(true);
        try {
            const countrySlug = cityPage?.countrySlug || city?.countrySlug;
            if (!countrySlug) return;

            const response = await fetch(`/api/cities-by-country?countrySlug=${countrySlug}&lang=${lang}`);
            if (response.ok) {
                const cities = await response.json();
                // Исключаем текущий город
                const currentSlug = cityPage?.citySlug || city?.slug?.current || city?.slug;
                const filteredCities = cities.filter(city =>
                    (city?.slug?.current || city?.slug) !== currentSlug
                );
                setAllCitiesInCountry(filteredCities);
                setVisibleCitiesCount(filteredCities.length);
            }
        } catch (error) {
            console.error('Ошибка загрузки городов:', error);
        } finally {
            setIsLoadingAllCities(false);
        }
    };

    // Deep-link scroll on mount - moved before any early returns to follow React hooks rules
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const hash = decodeURIComponent(window.location.hash || '').replace('#', '');
        if (!hash) return;
        const el = document.getElementById(hash);
        if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }, []);

    const isCityPageMissing = !cityPage;

    const {
        city,
        title,
        subtitle,
        hero,
        intro,
        toursShowcase,
        blogTeasers,
        mentionArticles,
        attractions,
        autoNearby,
        nearbyOverride,
        faq,
        countryAirports
    } = cityPage || {};

    const countrySlug = cityPage?.countrySlug || city?.countrySlug;

    // Координаты для клиентского WeatherBlock. Погода намеренно не приходит
    // из серверного рендера: её 6-часовой revalidate становился потолком TTL
    // всей страницы — см. src/app/api/weather/route.js.
    const weatherCoords = cityPage?.cityCoords || city?.coords || null;

    // Парсинг **bold** маркдауна внутри PortableText блоков
    const parseBoldInBlocks = (blocks) => {
        if (!Array.isArray(blocks)) return blocks;
        return blocks.map(block => {
            if (block._type !== 'block' || !Array.isArray(block.children)) return block;
            const hasMarkdown = block.children.some(c => c._type === 'span' && c.text?.includes('**'));
            if (!hasMarkdown) return block;
            const newChildren = [];
            block.children.forEach((child, idx) => {
                if (child._type !== 'span' || !child.text?.includes('**')) {
                    newChildren.push(child);
                    return;
                }
                child.text.split(/(\*\*[^*]+\*\*)/).forEach((part, pIdx) => {
                    if (!part) return;
                    if (part.startsWith('**') && part.endsWith('**')) {
                        newChildren.push({ ...child, _key: `${child._key || idx}_b${pIdx}`, text: part.slice(2, -2), marks: [...(child.marks || []), 'strong'] });
                    } else {
                        newChildren.push({ ...child, _key: `${child._key || idx}_p${pIdx}`, text: part });
                    }
                });
            });
            return { ...block, children: newChildren };
        });
    };

    const portableTextComponents = {
        marks: {
            providerLink: ({ children, value }) => {
                if (!value?.reference?.code) return <span>{children}</span>;
                const provider = value.reference;
                const href = buildProviderLink(provider, lang, countrySlug, {
                    pageSlug: `city-${citySlug}`,
                    position: 'content-link',
                });
                const isExternal = isExternalLink(href);
                if (isExternal) {
                    return (
                        <AffiliateLink
                            href={href}
                            provider={provider?.code}
                            position="content_link"
                            rel="nofollow noopener noreferrer sponsored"
                            className="text-blue-600 hover:text-blue-800 underline"
                        >
                            {children}
                        </AffiliateLink>
                    );
                }
                return (
                    <a
                        href={href}
                        className="text-blue-600 hover:text-blue-800 underline"
                    >
                        {children}
                    </a>
                );
            },
            cityLink: ({ children, value }) => {
                const slug = value?.reference?.slug?.current || value?.reference?.slug;
                const cSlug = value?.reference?.countrySlug || value?.reference?.country?.slug?.current;
                if (!slug || slug === 'null' || !cSlug || cSlug === 'null') return <span>{children}</span>;
                return (
                    <Link href={`/${lang}/${cSlug}/${slug}`} className="text-blue-600 hover:text-blue-800 underline">
                        {children}
                    </Link>
                );
            },
            poiLink: ({ children, value }) => {
                const poiSlug = value?.reference?.slug?.current || value?.reference?.slug;
                const poiCitySlug = value?.reference?.citySlug || value?.reference?.city?.slug?.current;
                const poiCountrySlug = value?.reference?.countrySlug || value?.reference?.country?.slug?.current;
                if (!poiSlug || poiSlug === 'null' || !poiCitySlug || poiCitySlug === 'null' || !poiCountrySlug || poiCountrySlug === 'null') return <span>{children}</span>;
                return (
                    <Link href={`/${lang}/${poiCountrySlug}/${poiCitySlug}/places/${poiSlug}`} className="text-blue-600 hover:text-blue-800 underline">
                        {children}
                    </Link>
                );
            },
            serviceLink: ({ children, value }) => {
                if (!value?.reference?.code) return <span>{children}</span>;
                const serviceCode = value.reference.code;
                if (!countrySlug) return <span>{children}</span>;
                return (
                    <Link href={`/${lang}/${countrySlug}/${serviceCode}`} className="text-blue-600 hover:text-blue-800 underline">
                        {children}
                    </Link>
                );
            },
            internalLink: ({ children, value }) => {
                const postSlug = value?.reference?.slug?.current || value?.reference?.slug;
                if (!postSlug) return <span>{children}</span>;
                return (
                    <Link href={`/${lang}/blog/${postSlug}`} className="text-blue-600 hover:text-blue-800 underline">
                        {children}
                    </Link>
                );
            },
            airportLink: ({ children, value }) => {
                if (!value?.reference?.iata) return <span>{children}</span>;
                const airCountrySlug = value.reference.countrySlug;
                if (!airCountrySlug) return <span>{children}</span>;
                return (
                    <Link href={`/${lang}/${airCountrySlug}/airport/${String(value.reference.iata).toLowerCase()}`} className="text-blue-600 hover:text-blue-800 underline">
                        {children}
                    </Link>
                );
            },
        }
    };

    // Обрабатываем новую структуру licensedImage: hero.image или cover.image вместо hero/cover
    const heroImageRaw = hero || city?.cover;
    const heroImage = unwrapImage(heroImageRaw);
    const heroBackground = heroImage
        ? urlFor(heroImage).width(1920).height(1080).fit('crop').url()
        : '/images/my-hero.jpg';
    // LCP-элемент: один файл 1920×1080 на телефоне грузился 6–9 с (обход 30.09)
    const heroSrcSet = heroImage ? resolveImageSrcSet(heroImage, [640, 960, 1280, 1920], 16 / 9) : undefined;
    const nearbyCities = nearbyOverride || autoNearby || city?.nearby || [];
    const cityDisplayName = title || city?.name;
    const currentCitySlug = cityPage?.citySlug || city?.slug?.current || city?.slug;
    // Ведём статьями, которые упоминают этот город (mentions-индекс), затем добираем
    // ручной подборкой (blogTeasers) — дедуп по _id, до 8. Так на странице города
    // всегда его релевантные статьи, а не общий дефолт.
    const formattedBlogTeasers = (() => {
        const seen = new Set();
        const out = [];
        for (const a of [...(Array.isArray(mentionArticles) ? mentionArticles : []), ...(Array.isArray(blogTeasers) ? blogTeasers : [])]) {
            const id = a?._id;
            if (id && !seen.has(id)) { seen.add(id); out.push(a); }
        }
        return out.slice(0, 8);
    })();

    // Обрабатываем FAQ с заменой плейсхолдеров
    // Важно: сохраняем PortableText структуру для answer, чтобы плейсхолдеры превращались в ссылки
    let faqItemsCountry = [];

    if (faq) {
        // Поддерживаем оба формата: массив объектов или уже обработанный формат
        const faqArray = Array.isArray(faq) ? faq : [];

        if (faqArray.length > 0) {
            faqItemsCountry = faqArray
                .map((item) => {
                    // Получаем вопрос и ответ, сохраняя PortableText структуру
                    // Поддерживаем оба формата: старый {q, a} и новый {question, answer}
                    const question = item?.q || item?.question || '';
                    const answer = item?.a || item?.answer;

                    // Проверяем, что есть и вопрос, и ответ
                    // answer может быть массивом PortableText, строкой или объектом
                    const hasAnswer = answer !== null &&
                        answer !== undefined &&
                        answer !== '' &&
                        (Array.isArray(answer) ? answer.length > 0 : true);

                    if (!question || !hasAnswer) {
                        return null;
                    }

                    return {
                        question,
                        answer
                    };
                })
                .filter((item) => item !== null && item.question);
        }
    }

    // Заменяем плейсхолдеры в FAQ, если есть данные
    // replacePlaceholdersDeep автоматически создаст ссылки для PortableText блоков
    if (gptData && faqItemsCountry.length > 0) {
        faqItemsCountry = faqItemsCountry.map((item) => ({
            question: typeof item.question === 'string'
                ? replacePlaceholdersDeep(item.question, gptData)
                : item.question,
            answer: parseBoldInBlocks(replacePlaceholdersDeep(item.answer, gptData))
        }));
    }

    // Заменяем плейсхолдеры в intro + парсим **bold**
    const introProcessed = parseBoldInBlocks(gptData && intro ? replacePlaceholdersDeep(intro, gptData) : intro);

    // Оверрайды времени от аэропортов из схемы City.travelTimeAirports (приоритет ниже ручных ETA из блока airports.manual)
    const travelTimeOverrides = useMemo(() => {
        const map = new Map();
        (city?.travelTimeAirports || []).forEach((item) => {
            const minutes = item?.minutes;
            if (typeof minutes !== 'number' || minutes <= 0) return;
            const slugKey = item?.airport?.slug;
            const iataKey = item?.airport?.iata?.toLowerCase?.();
            if (slugKey) map.set(slugKey, minutes);
            if (iataKey) map.set(iataKey, minutes);
        });
        return map;
    }, [city]);


    // Определяем, какие города показывать
    const citiesToShow = allCitiesInCountry.length > 0 ? allCitiesInCountry : nearbyCities;
    const isShowingAllCities = allCitiesInCountry.length > 0;

    const airportItems = useMemo(() => {
        // City.airports — опциональный блок. Если он не заполнен (старые импортированные
        // города), включаем авто-выбор ОДНОГО ближайшего аэропорта. Иначе аэропорт/секция
        // «как добраться» молча пропадали. У городов с явной настройкой (mode/count из Studio)
        // берётся их собственный count — этот дефолт на них не влияет.
        const selection = city?.airports || { mode: 'auto', count: 1 };
        const cityCoords = city?.coords;
        const cSlug = city?.slug?.current || city?.slug;
        const coSlug = city?.countrySlug;

        if (!cityCoords?.lat || !cityCoords?.lng) return [];

        let items = [];

        if (selection.mode === 'manual') {
            items = (selection.manual || [])
                .map((m) => {
                    const ap = m?.airport;
                    if (!ap?.slug || !ap?.iata || !ap?.coords?.lat || !ap?.coords?.lng) return null;

                    const distance = distanceKm(cityCoords, ap.coords) ?? null;
                    const overrideMinutes = travelTimeOverrides.get(ap.slug) ?? travelTimeOverrides.get(ap.iata?.toLowerCase?.());
                    const eta = m.etaMinutesOverride ?? overrideMinutes ?? (distance ? Math.round((distance / 45) * 60) : null);

                    return {
                        name: ap.name,
                        pageTitle: ap.pageTitle,
                        iata: ap.iata,
                        slug: ap.slug,
                        coords: ap.coords,
                        timeNote: m.timeNote,
                        eta,
                        distance,
                        countrySlug: coSlug,
                        citySlug: cSlug
                    };
                })
                .filter(Boolean);
        } else {
            const all = Array.isArray(countryAirports) ? countryAirports : [];
            items = all
                .filter((ap) => ap?.slug && ap?.iata && ap?.coords?.lat && ap?.coords?.lng)
                .map((ap) => {
                    const distance = distanceKm(cityCoords, ap.coords) ?? null;
                    const overrideMinutes = travelTimeOverrides.get(ap.slug) ?? travelTimeOverrides.get(ap.iata?.toLowerCase?.());
                    const eta = ap.etaMinutesOverride ?? overrideMinutes ?? (distance ? Math.round((distance / 45) * 60) : null);

                    return {
                        ...ap,
                        distance,
                        eta,
                        pageTitle: ap.pageTitle || ap.title,
                        countrySlug: coSlug,
                        citySlug: cSlug
                    };
                })
                .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
                .slice(0, Math.min(Math.max(selection.count || 2, 1), 3));
        }

        const seen = new Set();
        return items.filter((ap) => {
            if (seen.has(ap.slug)) return false;
            seen.add(ap.slug);
            return true;
        });
    }, [city, countryAirports, travelTimeOverrides]);

    // ── Travel facts shown next to the weather card (Gallery·Field mosaic) ──
    const isRuLang = lang === 'ru';
    const sp = t(lang, 'servicePage');   // общие тексты сервис-страниц
    const vp = t(lang, 'cityPageUi');     // специфика страницы города
    const basics = city?.countryBasics || {};
    const facts = useMemo(() => {
        const list = [];

        // Регион
        if (city?.region) {
            const r = (city?.countryRegions || []).find((x) => x?.value === city.region);
            const regionLabel = r ? (isRuLang ? r.nameRu : r.nameEn) : null;
            if (regionLabel) list.push({ k: vp.facts.region, v: regionLabel.trim() });
        }

        // Когда ехать (лучшие месяцы — диапазон(ы) с учётом перехода через год)
        const season = formatSeason(basics.bestMonths, vp.months, vp.season.yearRound);
        if (season) list.push({ k: vp.facts.whenToGo, v: season });

        // Ближайший аэропорт + расстояние (расстояние — на отдельной строке,
        // вся ячейка кликабельна и ведёт на страницу аэропорта)
        const ap = airportItems[0];
        if (ap) {
            const apName = shortAirportName(ap.pageTitle || ap.title || ap.name);
            if (apName) {
                const km = typeof ap.distance === 'number'
                    ? `${Math.round(ap.distance)} ${vp.facts.km}`
                    : null;
                const href = (ap.countrySlug && ap.iata)
                    ? `/${lang}/${ap.countrySlug}/airport/${String(ap.iata).toLowerCase()}`
                    : null;
                list.push({ k: vp.facts.airport, v: apName, sub: km, href });
            }
        }

        return list;
    }, [city, basics.bestMonths, airportItems, isRuLang, lang, vp]);

    // Местное время — живые часы, занимают широкую ячейку (последняя в сетке)
    const timeFact = basics.timeZone
        ? {
            k: vp.facts.localTime,
            timeZone: basics.timeZone,
            label: isRuLang ? (city?.countryNameRu || city?.countryName) : city?.countryName,
        }
        : null;

    if (isCityPageMissing) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-700 mb-4">
                        {vp.missing.title}
                    </h1>
                    <Link href={`/${lang}/tours`} className="text-blue-600 hover:underline">
                        {vp.missing.back}
                    </Link>
                </div>
            </div>
        );
    }

    // Туры из Sanity (документы `tour`, привязанные к городу) — как отели
    const sanityTours = (cityPage?.tours || []).filter(Boolean);

    const availability = {
        overview: Boolean(intro),
        tours: sanityTours.length > 0 || (Array.isArray(toursShowcase) && toursShowcase.length > 0),
        map: Array.isArray(attractions) && attractions.length > 0 && city?.coords?.lat && city?.coords?.lng,
        attractions: Array.isArray(attractions) && attractions.length > 0,
        hotels: (cityPage?.hotels || []).filter(Boolean).length > 0,
        articles: formattedBlogTeasers.length > 0,
        nearby: Array.isArray(nearbyCities) && nearbyCities.length > 0,
        faq: Array.isArray(faq) && faq.length > 0
    };

    // Подготавливаем данные для кнопки бронирования отелей
    const citySlug = cityPage?.citySlug || city?.slug?.current || city?.slug;
    const tripCityId = city?.stay?.tripCityId;
    const tripCountryId = city?.countryStay?.stay?.tripCountryId;
    const affiliateProvider = cityPage?.affiliateProviders?.find(p => p?.code === 'trip' && p?.active);

    // Проверяем, доступна ли кнопка бронирования (есть ли данные Trip.com)
    const hasStayButton = tripCityId && tripCountryId && affiliateProvider && countrySlug && citySlug;

    // Подготавливаем данные для кнопки
    const cityData = hasStayButton ? {
        ...city,
        stay: city?.stay
    } : null;

    const countryData = hasStayButton ? {
        ...city,
        stay: city?.countryStay?.stay
    } : null;

    const countryName = cityPage?.countryLabel || city?.countryName || '';

    const anchors = [
        availability.overview && { href: '#overview', label: vp.anchor.overview },
        availability.attractions && { href: '#attractions', label: vp.anchor.see },
        availability.map && { href: '#map', label: vp.anchor.map },
        { href: '#services', label: vp.anchor.services },
        availability.hotels && { href: '#hotels', label: vp.anchor.hotels },
        availability.tours && { href: '#tours', label: vp.anchor.tours },
        availability.faq && { href: '#faq', label: vp.anchor.faq },
        availability.articles && { href: '#articles', label: vp.anchor.articles },
        availability.nearby && { href: '#nearby', label: vp.anchor.nearby },
    ].filter(Boolean);

    const cityServices = [
        { key: 'esim', icon: <TbDeviceMobile />, ix: vp.services.esim.ix, title: vp.services.esim.title, desc: vp.services.esim.desc, go: vp.services.esim.go },
        { key: 'transport', icon: <TbCar />, ix: vp.services.transport.ix, title: vp.services.transport.title, desc: vp.services.transport.desc, go: vp.services.transport.go },
        { key: 'stay', icon: <TbBed />, ix: vp.services.stay.ix, title: vp.services.stay.title, desc: vp.services.stay.desc, go: vp.services.stay.go },
        { key: 'tours', icon: <TbCompass />, ix: vp.services.tours.ix, title: vp.services.tours.title, desc: vp.services.tours.desc, go: vp.services.tours.go },
    ];

    const str = (v) => (typeof v === 'string' ? v : '');
    const poiTitle = (item) => str(item.page?.h1) || str(item.nameEn) || str(item.title) || str(item.localName);
    // Map a POI to a GfBento tile. Inline POIs (no page/slug) get no href → render
    // as a non-clickable tile.
    const poiToBento = (item, i) => {
        const isInline = !item.page && !item.nameEn && (item.title || item.summary);
        const title = poiTitle(item);
        const summary = str(item.page?.subtitle) || str(item.summary);
        const category = str(item.category);
        const cover = item.cover?.image || item.cover || item.image;
        const img = cover ? urlFor(cover).width(760).height(480).fit('crop').url() : null;
        const srcSet = cover ? resolveImageSrcSet(cover) : null;
        const poiSlug = item.page?.poiSlug || item.slug?.current || item.slug;
        const href = (!isInline && poiSlug)
            ? buildPoiUrl(lang, item.countrySlug || countrySlug, item.citySlug || (city?.slug?.current || city?.slug), poiSlug)
            : null;
        return { id: item.id || `poi-${i}`, href, img, srcSet, alt: title, cat: category, title, excerpt: summary, meta: null };
    };

    // ── JSON-LD: город раньше не эмитил ничего (самый индексируемый location-шаблон) ──
    const cityCanonicalUrl = countrySlug && citySlug
        ? `https://www.triplinkhub.com/${lang}/${countrySlug}/${citySlug}`
        : null;
    const cityName = cityDisplayName || title;
    const cityNameForStay = lang === 'ru' ? declineCityName(cityDisplayName, 'prepositional') : cityDisplayName;
    let cityHeroImg;
    try { cityHeroImg = hero ? urlFor(unwrapImage(hero)).width(1200).height(630).fit('crop').url() : undefined; } catch { cityHeroImg = undefined; }
    const cityBreadcrumbJsonLd = cityCanonicalUrl ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: lang === 'ru' ? 'Главная' : 'Home', item: `https://www.triplinkhub.com/${lang}` },
            ...(countryName ? [{ '@type': 'ListItem', position: 2, name: countryName, item: `https://www.triplinkhub.com/${lang}/${countrySlug}` }] : []),
            { '@type': 'ListItem', position: countryName ? 3 : 2, name: cityName, item: cityCanonicalUrl },
        ],
    } : null;
    const cityDestinationJsonLd = cityCanonicalUrl ? {
        '@context': 'https://schema.org',
        '@type': 'City',
        '@id': `${cityCanonicalUrl}#city`,
        name: cityName,
        url: cityCanonicalUrl,
        ...(subtitle ? { description: subtitle } : {}),
        ...(cityHeroImg ? { image: cityHeroImg } : {}),
        ...(city?.coords?.lat && city?.coords?.lng ? { geo: { '@type': 'GeoCoordinates', latitude: city.coords.lat, longitude: city.coords.lng } } : {}),
        ...(countryName ? { containedInPlace: { '@type': 'Country', name: countryName } } : {}),
    } : null;
    const cityFaqJsonLd = faqItemsCountry.length > 0
        ? buildFaqJsonLd(faqItemsCountry, cityCanonicalUrl ? { id: `${cityCanonicalUrl}#faq` } : undefined)
        : null;
    const cityJsonLd = [cityBreadcrumbJsonLd, cityDestinationJsonLd, cityFaqJsonLd].filter(Boolean);

    return (
        <main className="gf-root">
            {cityJsonLd.map((node, i) => (
                <script key={`city-jsonld-${i}`} type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(node) }} />
            ))}
            {/* ── HERO ─────────────────────────────────────────── */}
            <section className="ct-hero">
                {heroBackground && <img src={heroBackground} srcSet={heroSrcSet} sizes="100vw" fetchPriority="high" alt={cityDisplayName} />}
                <div className="scrim" />
                <div className="ct-hero-in">
                    <h1>{cityDisplayName}</h1>
                    {subtitle && <p className="sub">{subtitle}</p>}
                    <div className="gf-hero-cta">
                        <Link className="pri" href={availability.hotels && countrySlug && citySlug ? `/${lang}/${countrySlug}/${citySlug}/hotels` : `/${lang}/${countrySlug}/stay`}>
                            {vp.heroStay(cityNameForStay)} <b>→</b>
                        </Link>
                        <Link className="sec" href={availability.tours ? '#tours' : `/${lang}/${countrySlug}/tours`}>
                            {vp.heroTours}
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── SUBNAV ───────────────────────────────────────── */}
            <GfSubnav lang={lang} leaf={cityDisplayName} anchors={anchors} crumbs={[
                countrySlug && countryName && { href: `/${lang}/${countrySlug}`, label: countryName },
                countrySlug && { href: `/${lang}/${countrySlug}/cities`, label: vp.crumbCities },
            ]} />

            {/* ── OVERVIEW + WEATHER ───────────────────────────── */}
            {intro && (
                <section className="gf-sec" id="overview">
                    <GfSectionMark eyebrow={vp.overviewEyebrow(cityDisplayName)} title={vp.overviewTitle} />
                    <div className="ct-prose">
                        <PortableText value={introProcessed} components={portableTextComponents} />
                    </div>
                    {(weatherCoords || facts.length > 0 || timeFact) && (
                        <WeatherBlock coords={weatherCoords} lang={lang} cityName={cityDisplayName} facts={facts} timeFact={timeFact} />
                    )}
                </section>
            )}

            {/* ── 02 · WHAT TO SEE ─────────────────────────────── */}
            {availability.attractions && (
                <section className="gf-sec" id="attractions" style={{ paddingTop: intro ? 0 : undefined }}>
                    <GfSectionMark
                        eyebrow={vp.seeEyebrow(cityDisplayName)}
                        title={vp.seeTitle}
                        allHref={countrySlug && currentCitySlug ? `/${lang}/${countrySlug}/${currentCitySlug}/places` : null}
                        allLabel={vp.seeAllLabel}
                    />
                    <p className="gf-lead">{vp.seeLead}</p>
                    <GfBento items={attractions.map(poiToBento)} bucket excerptAll />
                </section>
            )}

            {/* ── 03 · MAP ─────────────────────────────────────── */}
            {availability.map && (
                <section className="gf-sec" id="map" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.mapEyebrow(cityDisplayName)} title={vp.mapTitle} />
                    <p className="gf-lead">{vp.mapLead}</p>
                    <CityInteractiveMap
                        cityCoords={city.coords}
                        cityName={cityDisplayName}
                        attractions={attractions}
                        lang={lang}
                        zoom={13}
                        citySlug={city?.slug?.current || city?.slug}
                        countrySlug={city?.countrySlug}
                    />
                </section>
            )}

            {/* ── 04 · SERVICES IN CITY ────────────────────────── */}
            <section className="gf-sec" id="services" style={{ paddingTop: 0 }}>
                <GfSectionMark eyebrow={vp.servicesEyebrow(cityDisplayName)} title={vp.servicesTitle} />
                <p className="gf-lead">{vp.servicesLead}</p>
                <div className="ct-srv">
                    {cityServices.map((s) => (
                        <Link className="ctsrv" href={`/${lang}/${countrySlug}/${s.key}`} key={s.key}>
                            <span className="ix">{s.ix}</span>
                            <span className="ctsrv-head">
                                <span className="ctsrv-ic" aria-hidden>{s.icon}</span>
                                <h3>{s.title}</h3>
                            </span>
                            <p>{s.desc}</p>
                            <span className="go">{s.go}</span>
                        </Link>
                    ))}
                </div>
            </section>

            {/* ── 05 · HOTELS (Sanity) ─────────────────────────── */}
            <CityHotels
                hotels={cityPage?.hotels}
                lang={lang}
                countrySlug={countrySlug}
                citySlug={citySlug}
                cityDisplayName={cityDisplayName}
                cityName={city?.name}
                countryName={countryName}
                countryCurrency={city?.countryCurrency}
                countryData={countryData}
                cityData={cityData}
                affiliateProvider={affiliateProvider}
                hasStayButton={hasStayButton}
            />

            {/* ── 06 · TOURS (Sanity, как отели; toursShowcase — fallback) ── */}
            {sanityTours.length > 0 ? (
                <CityTours
                    tours={sanityTours}
                    lang={lang}
                    countrySlug={countrySlug}
                    citySlug={citySlug}
                    cityDisplayName={cityDisplayName}
                />
            ) : toursShowcase && toursShowcase.length > 0 && (
                <section className="gf-sec" id="tours" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.toursEyebrow(cityDisplayName)} title={vp.toursTitle} allHref={`/${lang}/${countrySlug}/tours`} allLabel={vp.toursAllLabel} />
                    <p className="gf-lead">{vp.toursLead}</p>
                    <div className="gf-arts">
                        {toursShowcase.map((tour, index) => {
                            const tourImage = unwrapImage(tour.image);
                            const tourImageUrl = tourImage ? urlFor(tourImage).width(760).height(480).url() : null;
                            const rawHref = tour.trackingUrlOverride || tour.baseUrl || '#';
                            const affiliate = getAffiliateMetadata(rawHref);
                            const href = affiliate ? wrapAffiliateLink(rawHref, {
                                provider: affiliate.provider,
                                subId: buildAffiliateSubId({
                                    language: lang,
                                    pageSlug: `city-${citySlug}`,
                                    position: 'city-tour-showcase',
                                }),
                            }) : rawHref;
                            return (
                                <AffiliateLink
                                    key={index}
                                    className="ar"
                                    href={href}
                                    provider={affiliate?.provider}
                                    vertical="tours"
                                    country={countrySlug}
                                    position="city-tour-showcase"
                                    label={tour.label}
                                >
                                    <div className="ph">
                                        {tourImageUrl && <img src={tourImageUrl} alt={tour.label} loading="lazy" />}
                                        <span className="tag">{vp.tourTag}</span>
                                    </div>
                                    <div className="ar-body">
                                        <h3>{tour.label}</h3>
                                        {tour.note && <p className="ex">{tour.note}</p>}
                                        <span className="read">{vp.tourView}</span>
                                    </div>
                                </AffiliateLink>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ── 08 · FAQ ─────────────────────────────────────── */}
            {faqItemsCountry.length > 0 && (
                <section className="gf-sec" id="faq" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.faqEyebrow(cityDisplayName)} tag={sp.tag.faq} title={sp.popularQuestions} />
                    <p className="gf-lead">{vp.faqLead}</p>
                    <CountryFAQ faqItems={faqItemsCountry} lang={lang} countryName={city?.countryName || cityDisplayName} countrySlug={countrySlug} variant="gf" />
                </section>
            )}

            {/* ── 09 · ARTICLES ────────────────────────────────── */}
            {formattedBlogTeasers.length > 0 && (
                <section className="gf-sec" id="articles" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.articlesEyebrow(cityDisplayName)} tag={sp.tag.blog} title={vp.articlesTitle} allHref={`/${lang}/blog?country=${countrySlug}`} allLabel={sp.allArticles} />
                    <GfArticleCards posts={formattedBlogTeasers} lang={lang} variant="bento" bucket />
                </section>
            )}

            {/* ── 10 · NEARBY CITIES ───────────────────────────── */}
            {citiesToShow.length > 0 && (
                <section className="gf-sec" id="nearby" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.nearbyEyebrow(cityDisplayName)} title={isShowingAllCities ? vp.nearbyTitleAll : vp.nearbyTitle} allHref={countrySlug ? `/${lang}/${countrySlug}/cities` : null} allLabel={vp.nearbyAllLabel} />
                    <p className="gf-lead">{isShowingAllCities ? vp.nearbyLeadAll : vp.nearbyLead}</p>
                    <GfBento items={citiesToShow.slice(0, visibleCitiesCount).map((nearbyCity) => {
                        const nearbyCoverImage = unwrapImage(nearbyCity.cover);
                        const nearbyCoverImageUrl = nearbyCoverImage ? urlFor(nearbyCoverImage).width(760).height(480).fit('crop').url() : null;
                        const nm = nearbyCity.label || nearbyCity.name;
                        return {
                            id: nearbyCity._id,
                            href: `/${lang}/${nearbyCity.countrySlug || cityPage?.countrySlug}/${nearbyCity.slug || nearbyCity.slug?.current}`,
                            img: nearbyCoverImageUrl,
                            srcSet: nearbyCoverImage ? resolveImageSrcSet(nearbyCoverImage) : null,
                            alt: nm,
                            cat: typeof nearbyCity.distanceKm === 'number' ? `${Math.round(nearbyCity.distanceKm)} ${vp.facts.km}` : null,
                            title: nm,
                            excerpt: nearbyCity.subtitle || null,
                            meta: null,
                        };
                    })} />
                    {nearbyCities.length >= defaultVisibleCities && (
                        <div className="gf-more-wrap">
                            <button
                                type="button"
                                className="gf-more"
                                disabled={isLoadingAllCities}
                                onClick={() => {
                                    if (isShowingAllCities) { setAllCitiesInCountry([]); setVisibleCitiesCount(defaultVisibleCities); }
                                    else { loadAllCitiesInCountry(); }
                                }}
                            >
                                {isLoadingAllCities ? vp.loading : isShowingAllCities ? vp.nearbyOnly : vp.showAll}
                            </button>
                        </div>
                    )}
                </section>
            )}

            {/* ── CTA ──────────────────────────────────────────── */}
            <GfCta
                eyebrow={vp.ctaEyebrow}
                title={vp.ctaTitle(cityDisplayName)}
            >
                <div className="gf-cta-btns">
                    <Link className="gf-btn" href={`/${lang}/${countrySlug}/esim`}>{vp.ctaConnectivity}</Link>
                    <Link className="gf-btn ghost" href={`/${lang}/${countrySlug}/stay`}>{vp.ctaStay}</Link>
                    <Link className="gf-btn ghost" href={`/${lang}/${countrySlug}/tours`}>{vp.ctaTours}</Link>
                </div>
            </GfCta>
        </main>
    );
}
